import { BackSide, BoxGeometry, CanvasTexture, CatmullRomCurve3, Color, DirectionalLight, EdgesGeometry, ExtrudeGeometry, Fog, Group, HemisphereLight, LineBasicMaterial, LineSegments, Mesh, MeshBasicMaterial, MeshStandardMaterial, PCFSoftShadowMap, PerspectiveCamera, PlaneGeometry, Scene, ShadowMaterial, Shape, Sprite, SpriteMaterial, TorusGeometry, TubeGeometry, Vector3, WebGLRenderer } from 'three';
const THREE = { BackSide, BoxGeometry, CanvasTexture, CatmullRomCurve3, Color, DirectionalLight, EdgesGeometry, ExtrudeGeometry, Fog, Group, HemisphereLight, LineBasicMaterial, LineSegments, Mesh, MeshBasicMaterial, MeshStandardMaterial, PCFSoftShadowMap, PerspectiveCamera, PlaneGeometry, Scene, ShadowMaterial, Shape, Sprite, SpriteMaterial, TorusGeometry, TubeGeometry, Vector3, WebGLRenderer };
/* <supernova-stage scene="hero|speed|race|round|computer|egld|close">: pinned three.js world; camera, lane count,
   lane speeds and time scale lerp between scenes. <validator-grid>: 2D canvas of 400 machines reaching quorum. */
(() => {
    const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const loadThree = async () => THREE;
  const LANES = 6, N = 34, STEP = 1.25;
  // hosts may write kebab attrs lowercased without the hyphen (round-ms → roundms); accept both
  const attr = (el, n) => el.getAttribute(n) ?? el.getAttribute(n.replace(/-/g, ''));
  const SCENES = {
    // chip: 1 → the lanes fold into a single chip (the computer); the icon from the Supernova film.
    hero:     { cam: [0.01, 30, .01],  look: [0, 0, 0],  gap: 2.4, lanes: 4, ts: 1,      speeds: null, chip: 1 },
    // before Supernova: the same lanes at the old 6 s cadence (time scale 1/10), seen from the side
    slow:     { cam: [9, 4, 14],      look: [0, 0, -4],   gap: 2.4, lanes: 4, ts: .1,     speeds: null },
    speed:    { cam: [1.5, 17, 5],    look: [0, 0, -7],   gap: 2.4, lanes: 4, ts: 1,      speeds: null },
    // cross-shard: shard 0, 1, 2 and the metachain side by side
    shards:   { cam: [10, 7.5, 15],   look: [0, 0, -6],   gap: 2.6, lanes: 4, ts: 1,      speeds: null },
    builders: { cam: [-6, 3, 12],     look: [0, 0, -8],   gap: 2.4, lanes: 4, ts: 1,      speeds: null },
    // zoom out of the lanes to the planet: 400 machines on every continent reach quorum in one round
    globe:    { cam: [0, 31, 6],      look: [-9, 30, -30], gap: 2.4, lanes: 4, ts: 1,      speeds: null, globe: 1 },
    round:    { cam: [-5.2, 1.7, 9],  look: [-3.6, .2, 4], gap: 2.4, lanes: 4, ts: 1 / 40, speeds: null },
    computer: { cam: [0, 12, 10],     look: [0, 0, -4],   gap: 1.15, lanes: 4, ts: .3,    speeds: null },
    egld:     { cam: [-8, 4.6, 13],   look: [0, 0, -6],   gap: 2.4, lanes: 4, ts: 1,      speeds: null },
    close:    { cam: [0.01, 30, .01],  look: [0, 0, 0],  gap: 2.4, lanes: 4, ts: 1,      speeds: null, chip: 1 }
  };

  class SupernovaStage extends HTMLElement {
    static get observedAttributes() { return ['round-ms', 'roundms', 'accent', 'paper', 'ink', 'scene', 'chip-frame', 'chipframe', 'time-scale', 'timescale']; }
    connectedCallback() {
      if (this._started) return; this._started = true;
      this.style.display = 'block'; this.style.width = '100%'; this.style.height = '100%'; this.style.minHeight = 'inherit';
      if (!this.style.position) this.style.position = 'relative';
      this.canvas = document.createElement('canvas');
      Object.assign(this.canvas.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'block' });
      this.appendChild(this.canvas);
      this.visible = true; this.mouse = { x: 0, y: 0 };
      this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; }); this.io.observe(this);
      this.onMove = e => { this.mouse.x = (e.clientX / innerWidth - .5) * 2; this.mouse.y = (e.clientY / innerHeight - .5) * 2; };
      window.addEventListener('pointermove', this.onMove, { passive: true });
      this.init().catch(err => console.warn('supernova-stage', err));
    }
    disconnectedCallback() { cancelAnimationFrame(this.raf); this.io?.disconnect(); this.ro?.disconnect(); window.removeEventListener('pointermove', this.onMove); this.renderer?.dispose(); }
    attributeChangedCallback() { this.applyAttrs(); }
    get roundMs() { return Math.max(150, +attr(this, 'round-ms') || 600); }
    get sceneDef() { return SCENES[this.getAttribute('scene')] || SCENES.hero; }
    applyAttrs() {
      if (!this.T) return;
      const T = this.T, paper = this.getAttribute('paper') || '#E4E4E0';
      this.scene.fog.color = new T.Color(paper);
      this.accent = new T.Color(this.getAttribute('accent') || '#23F7DD');
      this.ink = new T.Color(this.getAttribute('ink') || '#0A0A0A');
    }
    async init() {
      const T = this.T = await loadThree();
      const renderer = this.renderer = new T.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.shadowMap.enabled = false; renderer.shadowMap.type = T.PCFSoftShadowMap;
      const scene = this.scene = new T.Scene(); scene.fog = new T.Fog(0xe4e4e0, 14, 48);
            this.cam = new T.PerspectiveCamera(32, 1, .1, 120);
      const d = SCENES.hero; this.cam.position.set(...d.cam); this.look = new T.Vector3(...d.look);
      this.gap = d.gap; this.ts = d.ts; this.chip = 0; this.applyAttrs(); // chip eases in from 0 on first paint
      this.hardDot = this.dotTexture(T, false); this.softDot = this.dotTexture(T, true);
      this.buildChip(T, scene);
     
      scene.add(new T.HemisphereLight(0xffffff, 0x101010, .7));
      const sun = new T.DirectionalLight(0xffffff, 1.6); sun.position.set(6, 12, 8); sun.castShadow = true;
      sun.shadow.mapSize.set(2048, 2048); Object.assign(sun.shadow.camera, { left: -22, right: 22, top: 22, bottom: -22 }); sun.shadow.bias = -.0005; scene.add(sun);
      const ground = new T.Mesh(new T.PlaneGeometry(300, 300), new T.ShadowMaterial({ opacity: 0 }));
      ground.rotation.x = -Math.PI / 2; ground.position.y = -.5; ground.receiveShadow = true; scene.add(ground);
      const railMat = new T.MeshBasicMaterial({ color: 0x23f7dd, transparent: true, opacity: .35 });
      const geo = new T.BoxGeometry(.9, .9, .9), frameGeo = new T.BoxGeometry(1.15, 1.15, 1.15);
      const edgeGeo = new T.EdgesGeometry(geo), hullMat = new T.MeshBasicMaterial({ color: 0x23f7dd, side: T.BackSide }), edgeMat = new T.LineBasicMaterial({ color: 0x23f7dd });
      this.paper = new T.Color(0x0b0b0b); this.grey = new T.Color(0x23f7dd).multiplyScalar(.35);
      this.rails = []; this.blocks = []; this.heads = []; this.phase = []; this.speed = []; this.laneVis = [];
      for (let l = 0; l < LANES; l++) {
        const rail = new T.Mesh(new T.BoxGeometry(.06, .03, N * STEP + 6), railMat);
        rail.position.set(0, -.5, -N * STEP / 2 + 6); rail.receiveShadow = true; scene.add(rail); this.rails.push(rail);
        const row = [];
        for (let i = 0; i < N; i++) {
          // outline block: paper face + inverted-hull silhouette + hard edges. No solid fill.
          const m = new T.Group(), face = new T.Mesh(geo, new T.MeshStandardMaterial({ color: 0x0b0b0b, roughness: .4, metalness: .6 }));
          face.castShadow = true; face.receiveShadow = true;
          const hull = new T.Mesh(geo, hullMat); hull.scale.setScalar(1.05);
          m.add(face, hull, new T.LineSegments(edgeGeo, edgeMat)); m.userData.face = face.material;
          scene.add(m); row.push(m);
        }
        this.blocks.push(row);
        const f = new T.Mesh(frameGeo, new T.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true })); scene.add(f); this.heads.push(f);
        this.phase.push(l * .25); this.speed.push(1); this.laneVis.push(l < 4 ? 1 : 0);
      }
      this.ro = new ResizeObserver(() => { cancelAnimationFrame(this.rsz); this.rsz = requestAnimationFrame(() => this.resize()); }); this.ro.observe(this); this.resize();
      this.last = performance.now(); this.loop = this.loop.bind(this); this.raf = requestAnimationFrame(this.loop);
    }
    buildChip(T, scene) {
      const g = this.chipGroup = new T.Group(); scene.add(g);
      const S = 6.2 / 100, o = 50; // the notched chip outline, in a 100-unit box, scaled to 6.2 world units
      const pts = [[12,2],[19,2],[27,10.5],[32,12.5],[68,12.5],[73,10.5],[81,2],[88,2],[98,12],[98,88],[88,98],[81,98],[73,89.5],[68,87.5],[32,87.5],[27,89.5],[19,98],[12,98],[2,88],[2,12]];
      const shape = new T.Shape(); pts.forEach(([x, y], i) => { const px = (x - o) * S, py = (y - o) * S; i ? shape.lineTo(px, py) : shape.moveTo(px, py); }); shape.closePath();
      const depth = .5;
      const body = new T.Mesh(new T.ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelThickness: .05, bevelSize: .05, bevelSegments: 3 }),
        new T.MeshStandardMaterial({ color: 0x0f0f0f, roughness: .35, metalness: .7 }));
      body.rotation.x = -Math.PI / 2; body.position.y = -.25; body.castShadow = true; g.add(body);
      // teal rim: a tube along the outline, sitting on the top face
      const rimPts = shape.getPoints(4).map(p => new T.Vector3(p.x, depth - .25 + .07, p.y));
      const rim = new T.Mesh(new T.TubeGeometry(new T.CatmullRomCurve3(rimPts, true, 'catmullrom', 0), 400, .03, 8, true),
        new T.MeshBasicMaterial({ color: 0x23f7dd }));
      rim.renderOrder = 2; g.add(rim);
      // the X mark: two crossed teal bars; glow pulses with the round
      this.core = new T.Group(); this.core.position.y = depth - .25 + .1; g.add(this.core);
      const barMat = new T.MeshStandardMaterial({ color: 0x23f7dd, emissive: 0x23f7dd, emissiveIntensity: 1, roughness: .3 });
      // official MultiversX X mark (x-mint.svg path), extruded; 192×148 viewBox scaled to 2.6 world units wide
      const XS = 2.6 / 192, cx = 96, cy = 74, P = (x, y) => [(x - cx) * XS, (y - cy) * XS];
      const xs = new T.Shape();
      xs.moveTo(...P(106.4, 74)); xs.lineTo(...P(192, 28)); xs.lineTo(...P(177.6, .2)); xs.lineTo(...P(99.2, 32.1));
      xs.bezierCurveTo(...P(97.2, 32.9), ...P(94.9, 32.9), ...P(92.9, 32.1));
      xs.lineTo(...P(14.5, .2)); xs.lineTo(...P(.1, 28)); xs.lineTo(...P(85.7, 74)); xs.lineTo(...P(.1, 120)); xs.lineTo(...P(14.5, 147.8)); xs.lineTo(...P(92.9, 115.9));
      xs.bezierCurveTo(...P(94.9, 115.1), ...P(97.2, 115.1), ...P(99.2, 115.9));
      xs.lineTo(...P(177.6, 147.8)); xs.lineTo(...P(192, 120)); xs.lineTo(...P(106.4, 74)); xs.closePath();
      const xMark = new T.Mesh(new T.ExtrudeGeometry(xs, { depth: .16, bevelEnabled: false }), barMat);
      xMark.rotation.x = -Math.PI / 2; this.core.add(xMark);
      this.core.material = barMat;
      // the pipeline seen through the die: 4 faint lanes of blocks flowing at the round cadence, under the X
      this.mini = []; const MG = new T.BoxGeometry(.36, .36, .36), ME = new T.EdgesGeometry(MG);
      for (let l = 0; l < 4; l++) {
        const row = [];
        for (let i = 0; i < 12; i++) {
          const grp = new T.Group();
          const face = new T.Mesh(MG, new T.MeshBasicMaterial({ color: 0x23f7dd, transparent: true, opacity: .03 }));
          const edge = new T.LineSegments(ME, new T.LineBasicMaterial({ color: 0x23f7dd, transparent: true, opacity: .12 }));
          grp.add(face, edge); grp.userData = { face: face.material, edge: edge.material }; grp.position.set((l - 1.5) * .95, .32, 0);
          g.add(grp); row.push(grp);
        }
        this.mini.push(row);
      }
      g.traverse(o => { if (o.material) o.material.fog = false; });
      // PCB traces radiating from the chip on the board plane: thin teal segments with ring nodes, 45° jogs
      const tr = new T.Group(); g.add(tr);
      const nodeGeo = new T.TorusGeometry(.14, .025, 8, 24);
      this.traces = []; let cur = null; // { mat, pts[], len, pulse, offset, speed }
      let traceMat = null;
      let seed = 7; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
      const seg = (a, b) => {
        const len = a.distanceTo(b); if (len < .01) return;
        cur.pts.push(b.clone()); cur.len += len;
        const m = new T.Mesh(new T.BoxGeometry(len, .03, .05), traceMat);
        m.position.copy(a).add(b).multiplyScalar(.5); m.position.y = -.44; m.rotation.y = -Math.atan2(b.z - a.z, b.x - a.x); tr.add(m);
      };
      const trace = (x, z, dx, dz, steps) => {
        let p = new T.Vector3(x, 0, z);
        traceMat = new T.MeshBasicMaterial({ color: 0x23f7dd, transparent: true, opacity: .14 });
        cur = { mat: traceMat, pts: [p.clone()], len: 0, offset: rnd(), speed: .6 + rnd() * .8 };
        const pulse = new T.Sprite(new T.SpriteMaterial({ map: this.softDot, color: 0x23f7dd, transparent: true, opacity: .9, depthWrite: false, fog: false }));
        pulse.scale.setScalar(.4); cur.pulse = pulse; tr.add(pulse); this.traces.push(cur);
        for (let s = 0; s < steps; s++) {
          const straight = .5 + Math.floor(rnd() * 4) * .5;
          let q = p.clone(); q.x += dx * straight; q.z += dz * straight; seg(p, q); p = q;
          if (s < steps - 1) { const d = rnd() < .5 ? .5 : 1, side = rnd() < .5 ? -1 : 1; q = p.clone(); if (dx) { q.x += dx * d; q.z += side * d; } else { q.z += dz * d; q.x += side * d; } seg(p, q); p = q; }
        }
        const n = new T.Mesh(nodeGeo, traceMat); n.rotation.x = Math.PI / 2; n.position.set(p.x, -.44, p.z); tr.add(n); cur.node = n;
      };
      const half = 3.1 + .7;
      for (const side of [-1, 1]) for (let i = 0; i < 4; i++) trace(side * half, -1.8 + i * 1.2, side, 0, 2 + Math.floor(rnd() * 2));
      tr.traverse(o => { if (o.material) o.material.fog = false; });
    }
    dotTexture(T, soft) {
      const c = document.createElement('canvas'); c.width = c.height = 64; const x = c.getContext('2d');
      const g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
      if (soft) { g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(.25, 'rgba(255,255,255,.5)'); g.addColorStop(1, 'rgba(255,255,255,0)'); }
      else { g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(.7, 'rgba(255,255,255,1)'); g.addColorStop(1, 'rgba(255,255,255,0)'); }
      x.fillStyle = g; x.fillRect(0, 0, 64, 64); const t = new T.CanvasTexture(c); return t;
    }
    laneX(l, count) { return (l - (count - 1) / 2) * this.gap; }
    resize() {
      const w = this.clientWidth || 1, h = this.clientHeight || 1;
      this.renderer.setSize(w, h, false); this.cam.aspect = w / h; this.cam.fov = w < 720 ? 46 : 32; this.cam.updateProjectionMatrix();
    }
    loop(now) {
      this.raf = requestAnimationFrame(this.loop);
      try { this.tick(now); } catch (err) { if (!this._warned) { this._warned = true; console.warn('supernova-stage', err); } }
    }
    tick(now) {
      const dt = Math.min(50, now - this.last); this.last = now;
      if (!this.visible || document.hidden) return;
      const still = reduced(), def = this.sceneDef, rm = this.roundMs;
      // smooth scene parameters
      const k = 1 - Math.pow(.001, dt / 1000);
      this.gap += (def.gap - this.gap) * k * 1.2;
      this.chip += ((def.chip || 0) - this.chip) * k * 1.1;
      const chip = this.chip, ch = Math.max(.001, chip);
      // flat scenes show the 2D chip overlay instead of the 3D chip: lanes fold to the centre and vanish
      const cs = ch;
      this.chipGroup.scale.set(cs, cs, cs); this.chipGroup.visible = cs > .01;
      this.chipGroup.rotation.y = still ? 0 : (1 - chip) * .6;
      const pulse = this.phase[0] % 1; this.core.material.emissiveIntensity = .7 + Math.max(0, 1 - pulse) * .8;
      this.core.position.y = .35 + Math.sin(Math.PI * Math.max(0, 1 - pulse)) * .35;
      if (this.chipGroup.visible && this.traces) {
        const t = now / 1000;
        for (const tr of this.traces) {
          tr.mat.opacity = .1 + .08 * (.5 + .5 * Math.sin(t * 1.2 + tr.offset * 6.28));
          // position along the polyline: one run every (2 / speed) rounds, then rest
          const cycle = (this.phase[0] * .18 * tr.speed + tr.offset) % 1, u = Math.min(1, cycle / .55);
          let d = u * tr.len, a = tr.pts[0];
          for (let i = 1; i < tr.pts.length; i++) { const b = tr.pts[i], L = a.distanceTo(b); if (d <= L) { tr.pulse.position.lerpVectors(a, b, L ? d / L : 0); break; } d -= L; a = b; if (i === tr.pts.length - 1) tr.pulse.position.copy(b); }
          tr.pulse.position.y = -.4; tr.pulse.material.opacity = cycle < .55 ? .55 * Math.sin(Math.PI * u) : 0;
          if (tr.node) tr.node.scale.setScalar(1 + (cycle > .5 && cycle < .7 ? .6 * Math.sin(Math.PI * (cycle - .5) / .2) : 0));
        }
      }
      if (this.chipGroup.visible) {
        const MS = .46, span = 1.95, fadeW = .5;
        for (let l = 0; l < 4; l++) {
          const ph = this.phase[l] % 1, row = this.mini[l];
          for (let i = 0; i < row.length; i++) {
            const m = row[i], pos = i + ph, z = span - pos * MS, live = Math.max(0, 1 - Math.max(0, pos - 1));
            const fade = Math.max(0, Math.min(1, (span - Math.abs(z)) / fadeW));
            m.position.z = z; m.visible = z > -span && fade > 0;
            m.userData.face.opacity = (.03 + live * .14) * fade; m.userData.edge.opacity = (.1 + live * .35) * fade;
          }
        }
      }
      const tsAttr = parseFloat(attr(this, 'time-scale')), tsTarget = isFinite(tsAttr) && tsAttr > 0 ? tsAttr : def.ts;
      this.ts += (tsTarget - this.ts) * k * 1.4;
      // chip scenes: frame the chip into the DOM slot the page reports as "cy,frac" (viewport fractions)
      let tgt = def.cam, lk = def.look;
      const frame = def.chip && attr(this, 'chip-frame');
      if (frame) {
        const [cy, frac] = frame.split(',').map(Number);
        const half = Math.tan(this.cam.fov * Math.PI / 360), visPerDist = 2 * half;
        const d = (6.2 + 1.6) / (visPerDist * Math.max(.12, frac)); // chip + pin margin fills the slot height
        const L = -(cy - .5) * visPerDist * d;
        tgt = [0.01, d, L + .01]; lk = [0, 0, L];
      }
      const drift = still ? 0 : Math.sin(now / 9000) * .6;
      this.cam.position.x += (tgt[0] + drift + this.mouse.x * .8 - this.cam.position.x) * k;
      this.cam.position.y += (tgt[1] - this.mouse.y * .4 - this.cam.position.y) * k;
      this.cam.position.z += (tgt[2] - this.cam.position.z) * k;
      this.look.x += (lk[0] - this.look.x) * k; this.look.y += (lk[1] - this.look.y) * k; this.look.z += (lk[2] - this.look.z) * k;
      // keep the chip upright when looking straight down: screen-up becomes -z instead of the degenerate +y
      const dir = this.look.clone().sub(this.cam.position).normalize(), tUp = Math.min(1, Math.max(0, (-dir.y - .9) / .09));
      this.cam.up.set(0, 1 - tUp, -tUp).normalize();
      this.cam.lookAt(this.look);
      const count = def.lanes;
      for (let l = 0; l < LANES; l++) {
        const spTarget = def.speeds ? def.speeds[l] : 1;
        this.speed[l] = Math.exp(Math.log(this.speed[l]) + (Math.log(spTarget) - Math.log(this.speed[l])) * k * 1.5);
        if (!still) this.phase[l] += dt * this.ts / (rm * this.speed[l]);
        const visT = l < count ? 1 : 0; this.laneVis[l] += (visT - this.laneVis[l]) * k * 1.5;
        const vis = this.laneVis[l], x = this.laneX(l, count), ph = this.phase[l] % 1;
        this.rails[l].position.x = x; this.rails[l].scale.x = Math.max(.001, vis * (1 - chip)); this.rails[l].visible = vis > .02;
        const row = this.blocks[l];
        for (let i = 0; i < N; i++) {
          const m = row[i], pos = i + ph, live = Math.max(0, 1 - Math.max(0, pos - 1));
          m.position.set(x * (1 - chip), live * .55 * Math.sin(Math.PI * ph) * (1 - chip) + .35 * chip, (5 - pos * STEP) * (1 - chip));
          m.scale.setScalar((.8 + live * .2) * Math.max(.001, vis * (1 - chip)));
          m.userData.face.emissive.copy(this.accent).multiplyScalar(live * .55);
          m.userData.face.color.lerpColors(this.paper, this.grey, live);
          m.visible = pos < N - 1 && vis > .02;
        }
        const h = this.heads[l];
        h.position.set(x * (1 - chip), .1, (5 - ph * STEP) * (1 - chip)); h.rotation.y = still ? 0 : ph * Math.PI / 2;
        h.scale.setScalar(Math.max(.001, vis * (1 - chip))); h.visible = vis > .02;
        h.material.color.copy(this.accent); h.material.opacity = .25 + .65 * (1 - ph);
      }
      this.renderer.render(this.scene, this.cam);
    }
  }
  if (!customElements.get('supernova-stage')) customElements.define('supernova-stage', SupernovaStage);

  class ValidatorGrid extends HTMLElement {
    connectedCallback() {
      if (this._started) return; this._started = true;
      this.style.display = 'block'; this.style.width = '100%'; this.style.height = '100%'; this.style.minHeight = 'inherit';
      this.style.position = 'relative'; this.c = document.createElement('canvas'); Object.assign(this.c.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'block' });
      this.appendChild(this.c); this.ctx = this.c.getContext('2d');
      this.ro = new ResizeObserver(() => { cancelAnimationFrame(this.rsz); this.rsz = requestAnimationFrame(() => this.resize()); }); this.ro.observe(this);
      this.visible = true; this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; }); this.io.observe(this);
      const total = this.total; this.order = [...Array(total).keys()];
      let seed = 42; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
      for (let i = total - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [this.order[i], this.order[j]] = [this.order[j], this.order[i]]; }
      this.rank = new Array(total); this.order.forEach((v, i) => this.rank[v] = i);
      this.t0 = performance.now(); this.loop = this.loop.bind(this); this.raf = requestAnimationFrame(this.loop);
    }
    static get observedAttributes() { return ['slow']; }
    attributeChangedCallback() { this.t0 = performance.now(); }
    disconnectedCallback() { cancelAnimationFrame(this.raf); this.ro?.disconnect(); this.io?.disconnect(); }
    get total() { return +this.getAttribute('total') || 400; }
    get quorum() { return +this.getAttribute('quorum') || 267; }
    get roundMs() { return Math.max(150, +attr(this, 'round-ms') || 600); }
    resize() { const d = Math.min(devicePixelRatio, 2); this.c.width = this.clientWidth * d; this.c.height = this.clientHeight * d; this.d = d; }
    loop(now) {
      this.raf = requestAnimationFrame(this.loop);
      if (!this.visible || document.hidden || !this.d) return;
      const ctx = this.ctx, W = this.c.width, H = this.c.height, d = this.d;
      const accent = this.getAttribute('accent') || '#23F7DD', ink = this.getAttribute('ink') || '#E4E4E0', dim = this.getAttribute('dim') || 'rgba(228,228,224,.18)';
      const total = this.total, quorum = this.quorum, cols = 25, rows = Math.ceil(total / cols);
      const slow = +attr(this, 'slow') || 1; // show the round slowed down
      const rm = this.roundMs * slow, f = reduced() ? .8 : ((now - this.t0) % rm) / rm;
      ctx.clearRect(0, 0, W, H);
      const pad = 8 * d, cw = (W - pad * 2) / cols, ch = (H - pad * 2 - 18 * d) / rows, r = Math.min(cw, ch) * .32;
      const signed = f < .2 ? 0 : f < .75 ? Math.floor(((f - .2) / .55) * total) : total;
      for (let i = 0; i < total; i++) {
        const cx = pad + (i % cols) * cw + cw / 2, cy = pad + Math.floor(i / cols) * ch + ch / 2;
        const isSigned = this.rank[i] < signed;
        let fill = dim;
        if (f < .2) { const rip = Math.hypot(cx - W / 2, cy - H / 2) / Math.hypot(W / 2, H / 2); fill = rip < f / .2 ? 'rgba(228,228,224,.5)' : dim; }
        else if (isSigned) fill = f > .75 && f < .9 ? accent : (this.rank[i] < quorum ? ink : accent);
        else fill = 'rgba(228,228,224,.3)';
        ctx.beginPath(); ctx.arc(cx, cy, r * (isSigned && f >= .2 ? 1.15 : 1), 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill();
      }
      ctx.fillStyle = ink; ctx.font = `${11 * d}px ui-monospace, SFMono-Regular, Menlo, monospace`; ctx.textBaseline = 'bottom';
      const label = f < .2 ? 'proposal gossip' : f < .75 ? `${String(Math.min(signed, total)).padStart(3, '0')} / ${total} signed${signed >= quorum ? '  ·  quorum' : ''}` : f < .9 ? 'aggregate BLS proof + bitmap' : 'FINAL';
      ctx.fillText(label, pad, H - 2 * d);
      ctx.textAlign = 'right'; ctx.fillText(`${String(Math.floor(f * this.roundMs)).padStart(3, '0')} ms`, W - pad, H - 2 * d); ctx.textAlign = 'left';
    }
  }

  if (!customElements.get('validator-grid')) customElements.define('validator-grid', ValidatorGrid);
})();
